import { BarsBase } from '@/components/mic-selector/BarsBase';
import { useDictationClose } from '@/features/dictation/dictationBubble/hooks/useDictationClose';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { useAudioGraphStore } from '@/lib/audio/useAudioGraphStore';
import { cn } from '@/lib/cn';
import { useAudioDevices } from '@heidi/capture';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from '@heidi/design-system/select';
import { TypographyP2 } from '@heidi/design-system/typography';
import {
  bubbleExitAnimation,
  DICTATION_BUBBLE_Z_INDEX_CLASS,
  DictationCloseButton,
} from '@heidi/dictate';
import { isEmpty } from 'lodash-es';
import { AlertTriangle, Mic } from 'lucide-react';
import * as m from 'motion/react-m';
import { FormattedMessage, useIntl } from 'react-intl';

export const DictationMicSelector = () => {
  const currentDeviceLabel = useAudioGraphStore((state) => state.device?.label);
  const { deviceId, changeDeviceId, availableDevices } = useAudioDevices();
  const { handleClose, reset } = useDictationClose();
  const intl = useIntl();
  const isMobile = useIsMobile();

  const truncatedLabel =
    currentDeviceLabel && currentDeviceLabel.length > 20
      ? `${currentDeviceLabel.slice(0, 20)}...`
      : (currentDeviceLabel ?? '');

  const handleDeviceChange = async (d: string) => {
    await changeDeviceId(d);
    reset();
  };

  return (
    <m.div
      {...bubbleExitAnimation}
      className={cn(
        'group fixed inset-x-0 mx-auto flex w-fit min-w-[280px] max-w-[100vw] items-center gap-4 rounded-2xl border bg-surface-accent/80 p-4 text-sm backdrop-blur-sm newBrandDark:bg-surface-elevated/80',
        DICTATION_BUBBLE_Z_INDEX_CLASS,
        isMobile ? 'bottom-32' : 'bottom-6'
      )}
    >
      <DictationCloseButton onClose={handleClose} />
      <div className="flex items-center gap-3">
        <AlertTriangle size={20} className="shrink-0 text-validation-destructive" />
        <TypographyP2 className="shrink-0 text-validation-destructive">
          <FormattedMessage defaultMessage="No audio detected" id="v1xjVBcNV6" />
        </TypographyP2>
      </div>
      <div className="ml-auto">
        <Select value={deviceId ?? ''} onValueChange={(d) => void handleDeviceChange(d)}>
          <SelectTrigger
            className={
              'flex size-auto gap-1 border-transparent bg-transparent p-1 text-text-primary hover:bg-background-tertiary-pressed active:bg-background-tertiary-pressed'
            }
          >
            <div className="flex items-center gap-1">
              <Mic size={16} />
              {truncatedLabel && <TypographyP2>{truncatedLabel}</TypographyP2>}
              <BarsBase isSilent audioReady />
            </div>
          </SelectTrigger>
          <SelectContent align="end">
            {isEmpty(availableDevices) && (
              <TypographyP2 className="max-w-[200px] items-center p-4 text-center leading-none text-text-primary">
                <FormattedMessage
                  defaultMessage="No input device found. Please check your permissions or input devices and refresh the page"
                  id="gk2+jobt0X"
                />
              </TypographyP2>
            )}
            <SelectGroup>
              {availableDevices.map((device, index) => (
                <SelectItem key={device.deviceId} value={device.deviceId}>
                  {device.label
                    ? device.label
                    : intl.formatMessage(
                        {
                          defaultMessage: 'Microphone {index}',
                          id: 'Fih8xeZhPm',
                        },
                        { index: index + 1 }
                      )}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </m.div>
  );
};
